'use client';

/**
 * =============================================================================
 * ProductCarousel (also exported as TrendingProducts for backwards compat)
 * =============================================================================
 *
 * A unified product carousel component supporting multiple use cases:
 * - TrendingProducts (CMS-driven product list)
 * - AutomaticCarousel (filter-based automatic product selection)
 * - SliderProducts (Breitling's product slider with background image)
 *
 * =============================================================================
 * BREITLING COMPONENT MAPPING
 * =============================================================================
 *
 * This single component replaces THREE Breitling components:
 *
 * | Breitling Component | storefront-multibrand Equivalent |
 * |---------------------|------------------------|
 * | TrendingProducts    | ProductCarousel with CMS data |
 * | AutomaticCarousel   | ProductCarousel with automaticType + products |
 * | SliderProducts      | ProductCarousel with backgroundImage |
 *
 * =============================================================================
 * ARCHITECTURE NOTES
 * =============================================================================
 *
 * **Data Fetching:**
 * Breitling uses CLIENT-SIDE data fetching in AutomaticCarousel/SliderProducts.
 * We use SERVER-SIDE data fetching with products passed as props.
 *
 * Benefits of our approach:
 * - Better performance (no client waterfall)
 * - Better SEO (products in initial HTML)
 * - Simpler component (presentational only)
 * - Works with Next.js App Router patterns
 *
 * **Background Image:**
 * Breitling's SliderProducts uses <SliderOverlay> for background images.
 * We support this via `backgroundImage` prop with CSS positioning.
 *
 * =============================================================================
 * GOOGLE ANALYTICS INTEGRATION (STUBBED)
 * =============================================================================
 *
 * Breitling's SliderProducts sends GA4 `view_item_list` events:
 *
 * ```tsx
 * // Original Breitling pattern
 * const { sendViewItemListEvent } = useGoogleAnalytics();
 *
 * const sendViewItemEvent = (start: number, end: number) => {
 *   const skus = productsToShow.slice(start, end).map(p => p.sku);
 *   sendViewItemListEvent({
 *     skus,
 *     itemListName: heading,
 *   });
 * };
 *
 * // On mount
 * useEffect(() => { sendViewItemEvent(0, slidesNum); }, [productsHash]);
 *
 * // On slide change
 * onSlideChange={(swiper) => {
 *   sendViewItemEvent(prevIndex, swiper.activeIndex);
 * }}
 * ```
 *
 * TODO: Implement when GA4 integration is set up.
 * See: hooks/useGoogleAnalytics.ts (to be created)
 *
 * =============================================================================
 * AUTOMATIC CAROUSEL TYPES (from Breitling)
 * =============================================================================
 *
 * - 'recently viewed': Products from localStorage history
 * - 'compatible straps': Straps matching a watch reference
 * - 'you may also like': Recommendation algorithm
 * - 'trending now': Trending/featured products
 * - 'discover the collection': Collection showcase (links to collection)
 *
 * =============================================================================
 * USAGE EXAMPLES
 * =============================================================================
 *
 * ```tsx
 * // CMS Mode (TrendingProducts data from Contentful)
 * <ProductCarousel data={contentfulTrendingProducts} />
 *
 * // Automatic Mode - You May Also Like
 * const recommended = await fetchRecommendedProducts(currentProductId);
 * <ProductCarousel
 *   automaticType="you may also like"
 *   products={recommended}
 * />
 *
 * // With Background Image (SliderProducts style)
 * <ProductCarousel
 *   products={products}
 *   heading="Featured Collection"
 *   backgroundImage={{ url: '/images/bg.jpg', alt: 'Collection background' }}
 *   backgroundColor="#f5f5f5"
 * />
 * ```
 *
 * =============================================================================
 */

import { ProductCard, type ProductCardProps } from '@/components/brand';
import { Cta } from '@/components/cms/Card/components/Cta';
import { Heading, type HeadingData } from '@/components/cms/Heading';
import { useAddToCart } from '@/hooks/useAddToCart';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useWishlist } from '@/hooks/useWishlist';
import { IS_GALLET_SHAPED } from '@/lib/cms/config';
import type { CMSProductCard } from '@/lib/cms/types';
import {
  getGalletPlpProductCardProps,
  resolveCarouselProductKind,
} from '@/lib/product/galletPlpProductCardProps';
import { getProductPlaceholder } from '@/lib/product/placeholders';
import clsx from 'clsx';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { FreeMode, Navigation, Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import styles from './TrendingProducts.module.scss';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';

/**
 * Automatic carousel type (matches Breitling's SliderProductsTypeEnum)
 * Used to determine heading defaults and potential future behavior
 */
export type AutomaticCarouselType =
  | 'recently viewed'
  | 'compatible straps'
  | 'you may also like'
  | 'trending now'
  | 'discover the collection'
  | 'default';

/**
 * Product data structure for direct product passing
 * Matches ProductCardData from @/components/brand for full Breitling card rendering
 */
export interface ProductCarouselItem {
  id?: string;
  slug?: string;
  sku: string;
  name: string;
  subtitle?: string;
  price: number;
  /** Raw price amount for cart operations */
  priceAmount?: number;
  currency?: string;
  image: string;
  href: string;
  /** Collection name (for "discover the collection" type) */
  collection?: string;
  /** CTA data from OrderApp */
  variantCTA?: {
    type?: string;
    label?: string;
  };
  /** Launch date from Algolia (for filtering) */
  launch_date?: number;
  /** Saleor variant ID - required for add to cart */
  variantId?: string;
  /** CTA type from Algolia (ORDER, PRE_ORDER, NOTIFY, DISCOVER, NO_CTA, etc.) */
  ctaType?: string;
  /** Marketing label (New, Limited, etc.) */
  label?: string;
  /** Case material (e.g., "Stainless steel & 18k red gold") */
  material?: string;
  /** Product type for styling variations */
  productType?: 'Watch' | 'Strap' | 'Eyewear';
  /** Whether strap has integrated buckle (false = "Buckle not included" message) */
  integratedBuckle?: boolean;
}

/**
 * Background image configuration
 * Used for SliderOverlay-style backgrounds behind the carousel
 */
export interface CarouselBackgroundImage {
  /** Image URL */
  url: string;
  /** Alt text for accessibility */
  alt?: string;
  /** Image position (default: 'center') */
  position?: 'left' | 'center' | 'right';
  /** Object fit (default: 'cover') */
  objectFit?: 'cover' | 'contain';
  /** Opacity of background overlay (0-1, default: 0) */
  overlayOpacity?: number;
}

/**
 * Props for ProductCarousel component
 * Also exported as TrendingProductsProps for backwards compatibility
 */
export interface ProductCarouselProps {
  /**
   * Product data - accepts either ProductCarouselItem[] or CMSProductCard[] (from adapters)
   */
  products?: ProductCarouselItem[] | CMSProductCard[];
  /**
   * Automatic carousel type (from Breitling's AutomaticCarousel)
   * Affects default heading and potential future filtering logic
   */
  automaticType?: AutomaticCarouselType;
  /**
   * Heading text (legacy, plain string)
   * If not provided, defaults based on automaticType
   */
  heading?: string;
  /**
   * Structured heading data from CMS (includes headingType, headingSeoTag, colors)
   * Takes precedence over `heading` string when provided
   */
  headingData?: HeadingData;
  /** Optional extra data (e.g., from Saleor for real product data) */
  extraData?: Record<string, unknown>;
  /** Optional background color override */
  backgroundColor?: string;
  /**
   * Background image configuration (SliderOverlay equivalent)
   * Displays an image behind the carousel
   */
  backgroundImage?: CarouselBackgroundImage;
  /** Optional product card background color */
  productCardBackgroundColor?: string;
  /** Number of slides per view on desktop (default: 4) */
  slidesPerView?: number;
  /** Number of slides per view on mobile (default: 2.45) */
  slidesPerViewMobile?: number;
  /** Show product name (default: true) */
  showName?: boolean;
  /** Show product price (default: true) */
  showPrice?: boolean;
  /** Show CTA button (default: false) */
  showCta?: boolean;
  /** Show wishlist icon (default: false) */
  showWishlist?: boolean;
  /** Render the built-in heading (default: true). Set false to supply your own. */
  showHeading?: boolean;
  /** Show product label/badge (default: false) */
  showLabel?: boolean;
  /**
   * Card component override. Defaults to the brand router's `ProductCard`.
   * A brand whose card differs only inside this carousel passes it here rather
   * than changing the router, which every other card surface reads.
   *
   * Typed by props rather than `typeof ProductCard`, so an override need not
   * reproduce that component's compound statics.
   */
  ProductCardComponent?: ComponentType<ProductCardProps>;
  /** Optional className */
  className?: string;
  /** CMS index for unique data-testid generation */
  cmsIndex?: number;
  /**
   * Callback for GA tracking when items come into view
   * Called with array of SKUs currently visible
   * @stub This is not yet wired up - see GA integration notes above
   */
  onItemsView?: (skus: string[]) => void;
  /**
   * Bottom-of-carousel CTA. Mirrors legacy SliderProductsManual behaviour —
   * renders a button below the slider when `ctaText` is set and `ctaStyle`
   * is not `'none'`.
   */
  ctaText?: string | null;
  ctaUrl?: string | null;
  ctaStyle?: string | null;
  ctaAriaLabel?: string | null;
}

/** @deprecated Use ProductCarouselProps instead */
export type TrendingProductsProps = ProductCarouselProps;

/**
 * Default headings for automatic carousel types
 * Matches Breitling's SliderProductsTypeEnum labels
 */
const AUTOMATIC_TYPE_HEADINGS: Record<AutomaticCarouselType, string> = {
  'recently viewed': 'Recently Viewed',
  'compatible straps': 'Compatible Straps',
  'you may also like': 'You May Also Like',
  'trending now': 'Trending Now',
  'discover the collection': 'Discover the Collection',
  default: 'Products',
};

/**
 * Split-color heading patterns for Breitling brand
 * Format: { accentWords: number } - how many words to highlight in accent color
 *
 * Examples:
 * - "ADD AN EXTRA STRAP" → "ADD AN" (accent) + "EXTRA STRAP" (primary)
 * - "YOU MAY ALSO LIKE" → "YOU" (accent) + "MAY ALSO LIKE" (primary)
 * - "KEY FEATURES" → "KEY" (accent) + "FEATURES" (primary)
 */
const HEADING_ACCENT_PATTERNS: Record<string, number> = {
  'add an extra strap': 2, // "ADD AN"
  'you may also like': 1, // "YOU"
  'key features': 1, // "KEY"
  'the story': 1, // "THE"
  'trending now': 1, // "TRENDING"
  'recently viewed': 1, // "RECENTLY"
  'compatible straps': 1, // "COMPATIBLE"
  'discover the collection': 1, // "DISCOVER"
};

/**
 * Resolve the product-card background. When the section supplies its own
 * background image, cards default to transparent so the image shows through
 * behind them (no white block sticking out).
 *
 * This is intentional, brand-wide for Gallet: the `.section` wrapper here
 * unconditionally paints the topo-contour pattern via `::before` for
 * `html[data-brand='gallet']` (see `TrendingProducts.module.scss`) — every
 * Gallet `ProductCarousel`/`TrendingProducts` instance has that backdrop, so
 * there is no "plain white" Gallet section this would leave without a visible
 * boundary. An explicit CMS `productCardBackgroundColor` always wins;
 * Breitling (no topo backdrop) falls back to white.
 */
function resolveCardBackgroundColor(
  explicit: string | undefined,
  hasBackgroundImage: boolean
): string {
  if (explicit) return explicit;
  if (hasBackgroundImage || IS_GALLET_SHAPED) return 'transparent';
  return 'var(--color-white)';
}

function normalizeCarouselProducts(
  directProducts: ProductCarouselProps['products']
): ProductCarouselItem[] {
  if (!directProducts || directProducts.length === 0) {
    return [];
  }

  const firstProduct = directProducts[0];
  const isCMSFormat = 'sys' in firstProduct && firstProduct.sys?.id;

  if (isCMSFormat) {
    return (directProducts as CMSProductCard[]).map((product) => ({
      id: product.sys?.id,
      sku: product.sku || '',
      name: product.name || '',
      subtitle: product.subtitle || undefined,
      price: product.price || 0,
      priceAmount: product.price || undefined,
      currency: product.currency || 'CHF',
      image: product.image?.url || getProductPlaceholder(product.productType),
      href: product.href || '#',
      variantId: product.variantId || undefined,
      ctaType: product.ctaType || undefined,
      label: product.label || undefined,
      material: product.material || undefined,
      productType: product.productType || undefined,
      integratedBuckle: product.integratedBuckle ?? undefined,
    }));
  }

  return directProducts as ProductCarouselItem[];
}

interface CarouselHeadingConfig {
  headingText: string;
  heading: string;
  accentWordCount: number | undefined;
  headingColor: string | undefined;
  resolvedHeadingData: HeadingData | null;
}

function resolveCarouselHeading(
  headingDataProp: HeadingData | undefined,
  headingProp: string | undefined,
  automaticType: AutomaticCarouselType | undefined
): CarouselHeadingConfig {
  const headingText =
    headingDataProp?.text ||
    headingProp ||
    (automaticType ? AUTOMATIC_TYPE_HEADINGS[automaticType] : 'Trending Now');

  const accentWordCount =
    headingText && !IS_GALLET_SHAPED
      ? HEADING_ACCENT_PATTERNS[headingText.toLowerCase().trim()]
      : undefined;

  const headingColor = IS_GALLET_SHAPED ? 'var(--color-black)' : undefined;

  const resolvedHeadingData: HeadingData | null = headingText
    ? {
        ...headingDataProp,
        text: headingText,
        headingType: headingDataProp?.headingType ?? 'h2',
        headingSeoTag: headingDataProp?.headingSeoTag ?? 'h2',
      }
    : null;

  return {
    headingText,
    heading: headingText,
    accentWordCount,
    headingColor,
    resolvedHeadingData,
  };
}

function collectWishlistSkus(
  products: ProductCarouselItem[],
  isInWishlist: (sku: string) => boolean
): Set<string> {
  const skusInWishlist = new Set<string>();
  for (const product of products) {
    const sku = product.sku || product.id || '';
    if (sku && isInWishlist(sku)) {
      skusInWishlist.add(sku);
    }
  }
  return skusInWishlist;
}

/**
 * ProductCarousel - Unified product carousel component
 *
 * Also exported as TrendingProducts for backwards compatibility.
 *
 * @see File header for full documentation and usage examples
 */
export function ProductCarousel({
  products: directProducts,
  automaticType,
  heading: headingProp,
  headingData: headingDataProp,
  backgroundColor,
  backgroundImage,
  productCardBackgroundColor,
  slidesPerView = 4,
  slidesPerViewMobile: _slidesPerViewMobile = 2.45,
  showName = true,
  showPrice = true,
  showCta = false,
  showWishlist = true,
  showHeading = true,
  ProductCardComponent = ProductCard,
  className,
  cmsIndex,
  onItemsView,
  ctaText,
  ctaUrl,
  ctaStyle,
  ctaAriaLabel,
}: Readonly<ProductCarouselProps>) {
  const swiperRef = useRef<SwiperType | null>(null);
  const [hasTrackedInitialView, setHasTrackedInitialView] = useState(false);

  // ==========================================================================
  // Wishlist and Add to Cart hooks (same pattern as ProductGrid)
  // ==========================================================================
  const { isInWishlist, toggleItem } = useWishlist();
  const { addToCart, addingProductId } = useAddToCart();
  const { sendViewItemList } = useGoogleAnalytics();

  // Track wishlist status in state to avoid hydration mismatch
  // Server renders with empty set, client updates after mount
  const [wishlistSkus, setWishlistSkus] = useState<Set<string>>(new Set());
  const [hasMounted, setHasMounted] = useState(false);

  // Use ref to avoid isInWishlist causing infinite loops in useEffect
  const isInWishlistRef = useRef(isInWishlist);
  isInWishlistRef.current = isInWishlist;

  // ==========================================================================
  // Determine heading: headingData > string prop > automatic type default
  // ==========================================================================
  const { heading, accentWordCount, headingColor, resolvedHeadingData } = resolveCarouselHeading(
    headingDataProp,
    headingProp,
    automaticType
  );

  // ==========================================================================
  // Normalize products from props
  // Handles two formats:
  // 1. ProductCarouselItem[] - Already normalized (runtime fetched)
  // 2. CMSProductCard[] - From adapters (has sys.id, image as CMSMedia)
  // Memoized to prevent infinite re-renders
  // ==========================================================================
  const normalizedProducts = useMemo(
    () => normalizeCarouselProducts(directProducts),
    [directProducts]
  );

  // ==========================================================================
  // Test ID helpers
  // ==========================================================================
  const componentName = automaticType
    ? `carousel-${automaticType.replace(/\s+/g, '')}`
    : 'trendingproducts';
  const testIdPrefix = cmsIndex !== undefined ? `${componentName}-${cmsIndex}` : undefined;
  const testId = (suffix: string) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined);

  // ==========================================================================
  // GA Tracking
  // ==========================================================================
  /**
   * Get visible product SKUs for GA tracking
   * Based on current slide index and visible count
   */
  const getVisibleSkus = useCallback(
    (startIndex: number, count: number): string[] => {
      return normalizedProducts
        .slice(startIndex, startIndex + count)
        .map((p) => p.sku)
        .filter(Boolean);
    },
    [normalizedProducts]
  );

  /**
   * Track item view (GA4 view_item_list)
   */
  const trackItemsView = useCallback(
    (skus: string[]) => {
      if (skus.length === 0) return;

      if (onItemsView) {
        onItemsView(skus);
      }

      const visibleProducts = normalizedProducts.filter((product) => skus.includes(product.sku));
      if (visibleProducts.length > 0) {
        sendViewItemList(visibleProducts, heading);
      }
    },
    [heading, onItemsView, normalizedProducts, sendViewItemList]
  );

  // ==========================================================================
  // Track initial view on mount
  // ==========================================================================
  useEffect(() => {
    if (!hasTrackedInitialView && normalizedProducts.length > 0) {
      const initialSkus = getVisibleSkus(0, slidesPerView);
      trackItemsView(initialSkus);
      setHasTrackedInitialView(true);
    }
  }, [
    hasTrackedInitialView,
    normalizedProducts.length,
    getVisibleSkus,
    slidesPerView,
    trackItemsView,
  ]);

  // ==========================================================================
  // Sync wishlist status after hydration to avoid mismatch
  // ==========================================================================
  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (!hasMounted || normalizedProducts.length === 0) return;

    setWishlistSkus(collectWishlistSkus(normalizedProducts, isInWishlistRef.current));
  }, [hasMounted, normalizedProducts]);

  /**
   * Handle slide change for GA tracking
   * Tracks newly visible products when user scrolls
   */
  const handleSlideChange = useCallback(
    (swiper: SwiperType) => {
      const newIndex = swiper.activeIndex;
      const newSkus = getVisibleSkus(newIndex, slidesPerView);
      trackItemsView(newSkus);
    },
    [getVisibleSkus, slidesPerView, trackItemsView]
  );

  // ==========================================================================
  // Early return conditions (after all hooks are declared)
  // ==========================================================================
  // Matches Breitling's behavior: AutomaticCarousel returns null if no products
  if (normalizedProducts.length === 0) {
    return null;
  }

  // RecentlyViewed validation (matches Breitling)
  // Original: if (type === 'RecentlyViewed' && filter.slugs.length < 2) return null;
  if (automaticType === 'recently viewed' && normalizedProducts.length < 2) {
    // Don't show "Recently Viewed" with only 1 product
    return null;
  }

  const sectionStyle = backgroundColor ? { backgroundColor } : undefined;
  const isDiscoverCollection = automaticType === 'discover the collection';
  const hasBackgroundImage = !!backgroundImage?.url;

  const resolvedCardBackgroundColor = resolveCardBackgroundColor(
    productCardBackgroundColor,
    hasBackgroundImage
  );

  return (
    <section
      className={clsx(
        styles.section,
        // A background image (own) OR a caller-provided section background color
        // means the consumer controls the backdrop, so drop the brand topo
        // pattern that would otherwise double up behind the carousel.
        (hasBackgroundImage || !!backgroundColor) && styles.sectionWithBackground,
        className
      )}
      style={sectionStyle}
      data-testid={testId('section')}
    >
      {/* Background Image Layer (SliderOverlay equivalent) */}
      {hasBackgroundImage && (
        <div
          className={clsx(
            styles.backgroundImageWrapper,
            backgroundImage.position &&
              styles[
                `bgPosition${backgroundImage.position.charAt(0).toUpperCase() + backgroundImage.position.slice(1)}`
              ]
          )}
          data-testid={testId('background')}
        >
          <Image
            src={backgroundImage.url}
            alt={backgroundImage.alt || ''}
            fill
            className={styles.backgroundImage}
            style={{
              objectFit: backgroundImage.objectFit || 'cover',
            }}
            priority={false}
          />
          {/* Optional overlay for text readability */}
          {backgroundImage.overlayOpacity && backgroundImage.overlayOpacity > 0 && (
            <div
              className={styles.backgroundOverlay}
              style={{ opacity: backgroundImage.overlayOpacity }}
            />
          )}
        </div>
      )}

      <div className={clsx(styles.container, hasBackgroundImage && styles.containerOverImage)}>
        {showHeading && resolvedHeadingData && (
          <Heading
            Heading={resolvedHeadingData}
            numberOfColoredWords={accentWordCount}
            color={headingColor}
            className={styles.heading}
          />
        )}

        <div
          className={clsx(
            styles.sliderWrapper,
            normalizedProducts.length <= slidesPerView && styles.centered
          )}
        >
          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            onSlideChange={handleSlideChange}
            modules={[Navigation, Scrollbar, FreeMode]}
            spaceBetween={15}
            slidesPerView="auto"
            freeMode={{
              enabled: true,
              sticky: false,
            }}
            scrollbar={{
              draggable: true,
            }}
            navigation
            centeredSlides={false}
            centeredSlidesBounds={false}
            className={clsx(
              styles.swiper,
              normalizedProducts.length <= slidesPerView && styles.swiperCentered
            )}
            breakpoints={{
              768: {
                spaceBetween: 15,
              },
              992: {
                spaceBetween: 15,
              },
            }}
          >
            {normalizedProducts.map((product, index) => {
              const productSku = product.sku || product.id || '';
              return (
                <SwiperSlide
                  key={product.id || product.sku || index}
                  className={styles.productCard}
                  style={{ '--entry-stagger-index': index } as React.CSSProperties}
                  data-testid={testIdPrefix ? `${testIdPrefix}-product-${index}` : undefined}
                >
                  <ProductCardComponent
                    backgroundColor={resolvedCardBackgroundColor}
                    {...getGalletPlpProductCardProps(
                      resolveCarouselProductKind(product.productType, automaticType)
                    )}
                    listIndex={index}
                    itemListName={heading}
                    variant="compact"
                    product={{
                      id: product.id,
                      sku: product.sku,
                      // For "discover the collection", show collection name instead of product name
                      name:
                        isDiscoverCollection && product.collection
                          ? product.collection
                          : product.name,
                      subtitle: showName && !isDiscoverCollection ? product.subtitle : undefined,
                      price: showPrice && !isDiscoverCollection ? product.price : 0,
                      priceAmount: product.priceAmount,
                      currency: product.currency || 'CHF',
                      image: product.image || getProductPlaceholder(product.productType),
                      href: product.href,
                      // Breitling ProductCard fields for CTA, wishlist, and labels
                      variantId: product.variantId,
                      ctaType: product.ctaType,
                      label: product.label,
                      material: product.material,
                      productType: product.productType,
                      integratedBuckle: product.integratedBuckle,
                    }}
                    // Add to cart - uses built-in hook if product has variantId
                    onAddToBag={product.variantId ? addToCart : undefined}
                    isAddingToBag={addingProductId === (product.id || product.sku)}
                    showCta={showCta}
                    // Wishlist functionality - use deferred state to avoid hydration mismatch
                    showWishlist={showWishlist}
                    isInWishlist={wishlistSkus.has(productSku)}
                    onWishlistToggle={() => {
                      // Optimistically update local state for immediate feedback
                      setWishlistSkus((prev) => {
                        const next = new Set(prev);
                        if (next.has(productSku)) {
                          next.delete(productSku);
                        } else {
                          next.add(productSku);
                        }
                        return next;
                      });
                      toggleItem({
                        sku: productSku,
                        price: product.priceAmount || product.price || 0,
                        theCurrency: product.currency || 'USD',
                        type: product.productType?.toLowerCase() || 'watch',
                        ctaType: product.ctaType,
                        itemListName: heading,
                      });
                    }}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
        {ctaText && ctaStyle && ctaStyle !== 'none' && ctaUrl ? (
          <div className={styles.ctaWrapper} data-testid="trending-products-cta-wrapper">
            {/* aria-label comes from the localized dictionary inside <Cta>;
                ctaAriaLabel acts as an explicit per-entry override when set. */}
            <Cta
              url={ctaUrl}
              urlText={ctaText}
              urlType={ctaStyle}
              ariaLabel={ctaAriaLabel ?? undefined}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
}

ProductCarousel.displayName = 'ProductCarousel';

/**
 * @deprecated Use ProductCarousel instead
 * Alias for backwards compatibility
 */
export const TrendingProducts = ProductCarousel;
TrendingProducts.displayName = 'TrendingProducts';
